import React from 'react';
import {
  Box, Text, VStack, HStack, Progress, Badge, Icon, Alert, AlertIcon,
  Card, CardBody, Spinner, Button
} from '@chakra-ui/react';
import { FiCpu, FiCheckCircle, FiClock, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';

export default function ProcessingStatus({ 
  isProcessing, 
  progress, 
  status, 
  currentBatch, 
  totalBatches,
  processedCount,
  totalCount,
  error,
  onRefresh,
  isRefreshing = false
}) {
  // Don't show anything if not processing and no error
  if (!isProcessing && !error && progress === 0) {
    return null;
  }
  
  // Don't show if processing is complete and progress is 100%
  if (!isProcessing && progress === 100 && !error) {
    return null;
  }

  const getStatusColor = () => {
    if (error) return 'red';
    if (progress === 100) return 'green';
    return 'blue';
  };

  const getStatusIcon = () => {
    if (error) return FiAlertTriangle;
    if (progress === 100) return FiCheckCircle;
    if (isProcessing) return FiCpu;
    return FiClock;
  };

  const getStatusText = () => {
    if (error) return 'Processing Failed';
    if (progress === 100) return 'Processing Complete';
    if (isProcessing) return 'Processing in Progress';
    return 'Processing Status';
  };

  return (
    <Card variant="outline" borderColor={`${getStatusColor()}.200`} bg={`${getStatusColor()}.50`}>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* Header */}
          <HStack justify="space-between">
            <HStack>
              <Icon as={getStatusIcon()} color={`${getStatusColor()}.500`} />
              <Text fontWeight="bold" color={`${getStatusColor()}.700`}>
                {getStatusText()}
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Badge colorScheme={getStatusColor()} variant="solid">
                {progress}%
              </Badge>
              {onRefresh && (
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiRefreshCw} />}
                  onClick={onRefresh}
                  isLoading={isRefreshing}
                  isDisabled={isRefreshing}
                  variant="outline"
                  colorScheme={getStatusColor()}
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh Status'}
                </Button>
              )}
            </HStack>
          </HStack>

          {/* Progress Bar */}
          <Box>
            <Progress 
              value={progress} 
              size="lg" 
              colorScheme={getStatusColor()}
              borderRadius="md"
              hasStripe={isProcessing}
              isAnimated={isProcessing}
            />
          </Box>

          {/* Status Details */}
          <VStack spacing={2} align="stretch">
            {status && (
              <Text fontSize="sm" color="gray.600">
                {status}
              </Text>
            )}
            
            {currentBatch && totalBatches && (
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  Batch Progress
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {currentBatch} / {totalBatches}
                </Text>
              </HStack>
            )}
            
            {processedCount && totalCount && (
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  Tickets Processed
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {processedCount} / {totalCount}
                </Text>
              </HStack>
            )}
          </VStack>

          {/* Error Display */}
          {error && (
            <Alert 
              status={error.includes('Rate limit') ? "warning" : "error"} 
              variant="left-accent" 
              borderRadius="md"
            >
              <AlertIcon />
              <Text fontSize="sm">{error}</Text>
              {error.includes('Rate limit') && (
                <Text fontSize="xs" mt={1} color="orange.600">
                  Polling will automatically resume when rate limits are cleared.
                </Text>
              )}
            </Alert>
          )}

          {/* Loading Spinner */}
          {isProcessing && progress < 100 && (
            <HStack justify="center" spacing={2}>
              <Spinner size="sm" color={`${getStatusColor()}.500`} />
              <Text fontSize="sm" color="gray.600">
                Processing tickets...
              </Text>
            </HStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
}
